import { useTranslation } from 'react-i18next';
import { AlertTriangle, Bike } from 'lucide-react';
import type { DeliveryDispatchStatus } from '@/lib/delivery-providers-client';
import { cn } from '@/lib/utils';
import { DELIVERY_TONE_CLASSES, deliveryStatusKey, deliveryToneOf } from './delivery-status';

interface DeliveryStatusChipProps {
  status: DeliveryDispatchStatus;
  /** Shown after the status once a rider is assigned ("En camino · Juan"). */
  driverName?: string | null;
  /** Whether the run needs an operator (`dispatchNeedsAttention`); false mutes an ended run. */
  attention?: boolean;
  className?: string;
}

/** The rider's state on a card or in the detail header: status, plus who has the order when known. */
export function DeliveryStatusChip({ status, driverName, attention, className }: DeliveryStatusChipProps) {
  const { t } = useTranslation('orders');
  const tone = deliveryToneOf(status, attention);
  const Icon = tone === 'problem' ? AlertTriangle : Bike;
  const label = t(deliveryStatusKey(status));
  const driver = driverName?.trim();
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium',
        DELIVERY_TONE_CLASSES[tone],
        className,
      )}
      title={driver ? `${label} · ${driver}` : label}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="truncate">
        {label}
        {driver && <> · {driver}</>}
      </span>
    </span>
  );
}
